/**
 * The arrange step's day: the engine's day, with the visits in the order the user arranged them.
 *
 * The step has to draw a road for a day that has no trip and no plan, so it calls `POST /routes` with the
 * day's points. Which points those are is not this file's opinion: it is `assembleDay` from
 * `@trippool/core`, the same function the planner runs, called here with the same inputs the handler
 * builds server-side — the day's two anchors, the slots the user gave, and whether the party drives home
 * at noon.
 *
 * That is the whole point of the arrangement. A second implementation of "where the day starts, when it
 * comes home, which stop is last" would agree with the engine on the day it was written, and the picture
 * on screen would then be a promise the itinerary quietly stopped keeping.
 *
 * **The one thing that is deliberately not the engine's is the order of the visits**, and it has to be
 * this file's, because it is the only thing about the day the user is saying by hand. `assembleDay` sorts
 * a day that states its times into slot order — sáng first, tối last — and that is the engine's rule for
 * a plan, where it must hold. The list beside the map is `arrangement[day]`, whose order is the user's own
 * and has never been sorted, so a preview that took `assembleDay`'s order for the arrangement's showed the
 * slot order while the list showed the drag: measured on a four-stop day arranged tối, trưa, sáng, tối,
 * the pin numbered 1 opened the list's *third* row and the line ran 1 → 3 → 2 → 4. The step exists to let
 * the user decide that order, so the drag has to be the thing the map draws.
 *
 * The engine's ordering still applies where it is a promise, and that is now only the day the user *timed*:
 * `plan.ts` calls `assembleDay` for every generated day, a day that states its times comes back in slot order,
 * and an arranged day is routed in the order it is given — so the drag order is what an untimed day's plan
 * drives, and `fixed` below is that fact on this screen (which is why the step keeps saying so and stops
 * offering a reorder for the day whose own times already decided it).
 *
 * Two facts are counted rather than hidden. A place saved without coordinates is a complete asset that
 * simply cannot be drawn (AC-2.5), and a place deleted since the arrangement was written is not in the
 * list at all — both are said in words above the map instead of silently shortening the day.
 */

import { assembleDay, type DayPlace, type PlanStopKind } from "@trippool/core";

/** A place as the arrange step holds it: an id, a name, and where it is — or no location at all. */
export interface ArrangePlace {
  id: string;
  name: string;
  location: { lat: number; lon: number } | null;
}

export interface ArrangeDayStop {
  id: string;
  name: string;
  lat: number;
  lon: number;
  kind: PlanStopKind;
}

export interface ArrangeDay {
  /**
   * The day's stops: the visits in the order they were arranged, with the day's anchors around them.
   *
   * The visits are the arrangement's own order on every day, timed or not — that is what the map beside the
   * list has to draw. Whether *routing* may still improve on that order is `fixed`, below.
   */
  stops: ArrangeDayStop[];
  /**
   * Whether the times the user gave have already decided the day, so there is nothing left to suggest.
   *
   * True as soon as one stop carries a time of day, and it is the same condition the engine uses: a day the
   * user timed is visited in the order those times imply, and an optimiser that reordered it would schedule
   * dinner in the morning. What that means here is that the *plan* will be sorted into slot order — the
   * preview above is still the arrangement's order — so a reorder offered on this screen could never be
   * taken, and the step offers none (`arrangeOrder.ts` says so at length).
   */
  fixed: boolean;
  /** On the day and undrawable: no coordinate. */
  unlocatable: number;
  /** Named by the arrangement but gone from the place list — deleted since it was placed there. */
  missing: number;
}

/**
 * Assemble one day of the arrangement.
 *
 * `visits` may name places that are no longer in `known` — an arrangement outlives the places it mentions
 * — so the caller passes the lookup rather than pre-filtering, and the count of what was dropped comes
 * back with the stops.
 */
export function assembleArrangeDay(input: {
  /** The day's places in the arrangement's own order, each with the slot it was given. */
  visits: ReadonlyArray<{ placeId: string; slot?: string | undefined }>;
  /** The day's own start: the previous night's lodging, or the trip's entry point on day 1. */
  from: { placeId: string; kind: "lodging" | "entry" } | null;
  /** That night's lodging. */
  to: string | null;
  returnToLodging: boolean;
  /** Every place the step knows about, by id: the ticked places and the lodgings both. */
  known: ReadonlyMap<string, ArrangePlace>;
}): ArrangeDay {
  /*
   * The map pairs a `PlanPlace` with the row it came from, keyed by object identity rather than by id.
   *
   * Identity because the same place legitimately appears twice in one day — a lodging is where the party
   * sleeps and where it returns to rest, and those are two stops with two meanings — so an id-keyed map
   * would answer for the wrong row.
   */
  const source = new Map<DayPlace, ArrangePlace>();
  let unlocatable = 0;
  let missing = 0;

  /** A place the day can route through, or null with the reason counted. */
  const routable = (placeId: string): ArrangePlace | null => {
    const place = input.known.get(placeId);
    if (place === undefined) {
      missing += 1;
      return null;
    }
    if (place.location === null) {
      unlocatable += 1;
      return null;
    }
    return place;
  };

  const toDay = (place: ArrangePlace): DayPlace => {
    const point: DayPlace = { id: place.id, name: place.name, lat: place.location!.lat, lon: place.location!.lon };
    source.set(point, place);
    return point;
  };

  const visits: Array<{ place: DayPlace; slot?: string | undefined }> = [];
  for (const visit of input.visits) {
    const place = routable(visit.placeId);
    if (place === null) continue;
    visits.push(visit.slot === undefined ? { place: toDay(place) } : { place: toDay(place), slot: visit.slot });
  }

  const anchor = (placeId: string | null): ArrangePlace | null => (placeId === null ? null : routable(placeId));
  const fromPlace = input.from === null ? null : anchor(input.from.placeId);
  const toPlace = anchor(input.to);

  const { points, kinds } = assembleDay({
    from: fromPlace === null || input.from === null ? null : { place: toDay(fromPlace), kind: input.from.kind },
    to: toPlace === null ? null : toDay(toPlace),
    visits,
    returnToLodging: input.returnToLodging,
  });

  /*
   * The visits back into the arrangement's own order, with every anchor left where the engine put it.
   *
   * `points` comes back sorted by slot (`assembleDay`'s rule for a day that states its times), and taking
   * it as the arrangement is the defect this block exists to prevent. What is kept from it is the day's
   * *shape* — the visit list is a permutation of `visits` and nothing else is — so the visit seats are
   * refilled from `visits`, which is the order `visits` was given in, which is the order the list renders.
   * `kinds` needs no repair: every seat being refilled is a visit on both sides, and the anchors are the
   * seats this mapping does not touch. So the pins are numbered 1..n over the visits in the arrangement's
   * order, and pin k is list row k by construction rather than by two orderings agreeing.
   *
   * The midday return is the one anchor whose *seat* is computed from the slots (`assembleDay` puts it
   * before the first afternoon stop). Its position among the visits is therefore the engine's, which is
   * right for it: when the party comes home at noon is a fact about the day's times, not about the order
   * the user dragged, and it carries a hotel glyph rather than a number.
   */
  let seat = 0;
  const arranged = points.map((point, index) => {
    if (kinds[index] !== "visit") return point;
    const visit = visits[seat]!;
    seat += 1;
    return visit.place;
  });

  return {
    stops: arranged.map((point, index) => {
      const place = source.get(point)!;
      return {
        id: place.id,
        name: place.name,
        lat: place.location!.lat,
        lon: place.location!.lon,
        kind: kinds[index]!,
      };
    }),
    fixed: input.visits.some((visit) => visit.slot !== undefined),
    unlocatable,
    missing,
  };
}
